// Texture Forge screen (FRG-1, ARCH §21). A parameter UI over the pure procedural
// painter (render/textures/forge.ts): tweak the base/accent palette, noise kind,
// octaves, scale, dither and edge-shade and watch a live 16×16 preview. "Forge"
// serializes the params to a shareable seed string; "Apply" re-skins the
// `forged_block` cosmetic variant (creative-only) and hands the player one; a
// pasted seed string imports someone else's skin. Client-only (ENG-1); the model
// it drives is hermetic and deterministic.
import { h, clear } from '../h';
import {
  type ForgeParams, type ForgeNoise, type RGB,
  clampForgeParams, forgeImageData, packSeed, unpackSeed,
  FORGE_BASE_MIN, FORGE_BASE_MAX, FORGE_OCTAVES_MIN, FORGE_OCTAVES_MAX,
  FORGE_SCALE_MIN, FORGE_SCALE_MAX, TILE_PX,
} from '../../render/textures/forge';
import type { Game } from '../../boot';

const PREVIEW_SCALE = 8; // 16px tile → 128px preview

const toHex = (c: RGB): string =>
  '#' + c.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
const fromHex = (hex: string): RGB => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
};

export function openForge(game: Game): void {
  let params = clampForgeParams(game.forgeParams());

  game.uiOpen = true;
  document.exitPointerLock();

  const overlay = h('div', { class: 'bb-window-overlay' });
  const panel = h('div', { class: 'bb-window bb-forge', 'data-testid': 'forge-screen' });
  overlay.append(panel);
  document.body.append(overlay);

  // ── Live preview (a 16×16 canvas shown pixel-scaled). ──
  const preview = h('canvas', {
    class: 'bb-forge-preview', 'data-testid': 'forge-preview',
    width: TILE_PX, height: TILE_PX,
    style: { width: `${TILE_PX * PREVIEW_SCALE}px`, height: `${TILE_PX * PREVIEW_SCALE}px`, imageRendering: 'pixelated' },
  }) as HTMLCanvasElement;
  const pctx = preview.getContext('2d')!;

  const seedOut = h('input', { class: 'bb-input bb-forge-seed', 'data-testid': 'forge-seed', readonly: true, spellcheck: false }) as HTMLInputElement;
  const msg = h('div', { class: 'bb-forge-msg', 'data-testid': 'forge-msg' });

  const redraw = (): void => {
    params = clampForgeParams(params);
    pctx.putImageData(forgeImageData(params), 0, 0);
    seedOut.value = packSeed(params);
  };

  // ── Palette controls ──
  const paletteWrap = h('div', { class: 'bb-forge-palette' });
  const renderPalette = (): void => {
    clear(paletteWrap);
    params.base.forEach((col, i) => {
      const picker = h('input', { type: 'color', value: toHex(col), 'data-testid': `forge-base-${i}`, class: 'bb-forge-color' }) as HTMLInputElement;
      picker.oninput = () => { params = { ...params, base: params.base.map((c, j) => (j === i ? fromHex(picker.value) : c)) }; redraw(); };
      paletteWrap.append(picker);
    });
    const canRemove = params.base.length > FORGE_BASE_MIN;
    const canAdd = params.base.length < FORGE_BASE_MAX;
    const rm = h('button', { class: 'bb-btn bb-forge-mini', 'data-testid': 'forge-base-remove', disabled: !canRemove }, '−') as HTMLButtonElement;
    rm.onclick = () => { if (params.base.length > FORGE_BASE_MIN) { params = { ...params, base: params.base.slice(0, -1) }; renderPalette(); redraw(); } };
    const add = h('button', { class: 'bb-btn bb-forge-mini', 'data-testid': 'forge-base-add', disabled: !canAdd }, '+') as HTMLButtonElement;
    add.onclick = () => { if (params.base.length < FORGE_BASE_MAX) { params = { ...params, base: [...params.base, params.base[params.base.length - 1]!] }; renderPalette(); redraw(); } };
    paletteWrap.append(rm, add);
  };

  const accent = h('input', { type: 'color', value: toHex(params.accent), 'data-testid': 'forge-accent', class: 'bb-forge-color' }) as HTMLInputElement;
  accent.oninput = () => { params = { ...params, accent: fromHex(accent.value) }; redraw(); };

  const noise = h('select', { class: 'bb-input', 'data-testid': 'forge-noise' },
    ...(['value', 'simplex', 'checker'] as ForgeNoise[]).map((k) =>
      h('option', { value: k, selected: params.noise === k }, k))) as HTMLSelectElement;
  noise.onchange = () => { params = { ...params, noise: noise.value as ForgeNoise }; redraw(); };

  // Labelled range helper.
  const rangeRow = (
    label: string, testid: string, min: number, max: number, step: number,
    get: () => number, set: (v: number) => void,
  ): HTMLElement => {
    const lbl = h('div', { class: 'bb-label' }, `${label}: ${get()}`);
    const input = h('input', { class: 'bb-input', type: 'range', 'data-testid': testid, min: String(min), max: String(max), step: String(step), value: String(get()) }) as HTMLInputElement;
    input.oninput = () => { set(Number(input.value)); lbl.textContent = `${label}: ${get()}`; redraw(); };
    return h('div', { class: 'bb-forge-row' }, lbl, input);
  };

  const octaves = rangeRow('Octaves', 'forge-octaves', FORGE_OCTAVES_MIN, FORGE_OCTAVES_MAX, 1,
    () => params.octaves, (v) => { params = { ...params, octaves: v }; });
  const scale = rangeRow('Scale', 'forge-scale', FORGE_SCALE_MIN, FORGE_SCALE_MAX, 1,
    () => params.scale, (v) => { params = { ...params, scale: v }; });
  const dither = rangeRow('Dither', 'forge-dither', 0, 1, 0.01,
    () => params.dither, (v) => { params = { ...params, dither: v }; });
  const edgeShade = rangeRow('Edge shade', 'forge-edgeshade', 0, 1, 0.01,
    () => params.edgeShade, (v) => { params = { ...params, edgeShade: v }; });

  // ── Import a pasted seed string ──
  const importIn = h('input', { class: 'bb-input bb-forge-import', 'data-testid': 'forge-import', placeholder: 'Paste a seed string…', spellcheck: false }) as HTMLInputElement;
  const importBtn = h('button', { class: 'bb-btn', 'data-testid': 'forge-import-btn' }, 'Import') as HTMLButtonElement;
  importBtn.onclick = () => {
    try {
      params = unpackSeed(importIn.value);
      renderPalette();
      accent.value = toHex(params.accent);
      noise.value = params.noise;
      redraw();
      msg.textContent = 'Imported skin.';
    } catch (e) {
      msg.textContent = `Import failed: ${(e as Error).message}`; // E-33 surfaced, never a crash
    }
  };

  // ── Apply: re-skin the forged_block variant + hand the player one ──
  const applyBtn = h('button', { class: 'bb-btn bb-primary', 'data-testid': 'forge-apply' }, 'Forge & Apply') as HTMLButtonElement;
  applyBtn.onclick = () => {
    game.applyForge(params);
    msg.textContent = 'Forged! A forged_block was added to your inventory.';
  };

  // Hangar §39.5 (M105.3) — `.tfskin` through the Hangar pickers (or a download,
  // unbridged): a `.tfskin` file's content IS the same packed seed string Import-by-
  // paste already understands, so export/import reuse that codec rather than a new one.
  const exportBtn = h('button', { class: 'bb-btn', 'data-testid': 'forge-export' }, 'Export') as HTMLButtonElement;
  exportBtn.onclick = () => {
    void (async () => {
      const { exportFile } = await import('../../hangar/files');
      const bytes = new TextEncoder().encode(packSeed(params));
      await exportFile(game.hangar, bytes, 'skin.tfskin', ['.tfskin'], 'text/plain');
      msg.textContent = 'Exported skin.';
    })();
  };
  const importFileBtn = h('button', { class: 'bb-btn', 'data-testid': 'forge-import-file' }, 'Import file…') as HTMLButtonElement;
  importFileBtn.onclick = () => {
    void (async () => {
      const { importFile } = await import('../../hangar/files');
      const bytes = await importFile(game.hangar, ['.tfskin']);
      if (!bytes) return;
      try {
        params = unpackSeed(new TextDecoder().decode(bytes));
        renderPalette();
        accent.value = toHex(params.accent);
        noise.value = params.noise;
        redraw();
        msg.textContent = 'Imported skin.';
      } catch (e) {
        msg.textContent = `Import failed: ${(e as Error).message}`; // E-33, never a crash
      }
    })();
  };

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey, true);
    overlay.remove();
    game.uiOpen = false;
    void game.renderer.domElement.requestPointerLock();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (document.activeElement === importIn) return;
    if (e.code === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
  };
  document.addEventListener('keydown', onKey, true);
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });

  panel.append(
    h('div', { class: 'bb-window-title' }, 'Texture Forge'),
    h('div', { class: 'bb-forge-body' },
      h('div', { class: 'bb-forge-preview-wrap' }, preview),
      h('div', { class: 'bb-forge-controls' },
        h('div', { class: 'bb-label' }, 'Base palette'), paletteWrap,
        h('div', { class: 'bb-forge-row' }, h('div', { class: 'bb-label' }, 'Accent'), accent),
        h('div', { class: 'bb-forge-row' }, h('div', { class: 'bb-label' }, 'Noise'), noise),
        octaves, scale, dither, edgeShade,
      ),
    ),
    h('div', { class: 'bb-label' }, 'Seed string'), seedOut,
    h('div', { class: 'bb-forge-row' }, importIn, importBtn),
    h('div', { class: 'bb-forge-actions' }, applyBtn, exportBtn, importFileBtn, h('button', { class: 'bb-btn', onclick: close }, 'Close')),
    msg,
  );

  renderPalette();
  redraw();
}

export type { ForgeParams };
